import { Dialog, Grid, Menu, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, Theme, Typography } from "@mui/material";
import { createStyles, StyledComponentProps, withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { ChartJsHeight, ChartJsOptions, DefaultListParameters } from "assets/appConfig";
import { DashboardThemeClassKey, GlobalClassKeys } from "assets/jss/common-styles";
import { AppMessageBox } from "components/AppMessageBox";
import ButtonDropdown from "components/Buttons/ButtonDropdown";
import { SpinnerIndicator } from "components/CallServer";
import IconAwardPoints from "components/Icons/IconAwardPoints";
import IconTrophyAlt from "components/Icons/IconTrophyAlt";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiAvatar from "components/MuiExtras/MuiAvatar";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiLink from "components/MuiExtras/MuiLink";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiPaper from "components/MuiExtras/MuiPaper";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import AwardTypeName from "components/Translations/AwardTypeName";
import UserFullProfileLink from "components/UserFullProfileLink";
import UserProfileImageLink from "components/UserProfileImageLink";
import { Formaters } from "lib/Formarters";
import { PublicationType } from "models/ApiEnums";
import { AwardHistoryDto, AwardHistoryGraphDto, AwardRankingListResult, PagedResult, UserBadgeDto } from "models/ApiModels";
import moment from "moment";
import * as React from "react";
import { Bar } from 'react-chartjs-2';
import { withTranslation, WithTranslation } from "react-i18next";
import WonBadgeInfoDialog from "views/Gamification/Dialogs/WonBadgeInfoDialog";
import { getPublicationSecondViewAction, getTopicSecondViewAction } from "views/Timeline/Components/FeedHelpers";
import UserShortProfile from "views/User/Components/UserShortProfile";

/////////////////////////////////////////////////////////////////////////////////////

type MonthYear = {
    key: number;
    text: string;
};

interface IProps {
    userId: string;
}

type CommonProps = IProps & StyledComponentProps<DashboardThemeClassKey> & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

interface IUserAwardsAccountStatementComponentState {
    dialog: any;
    data: PagedResult<AwardHistoryDto>;
    graphData: AwardHistoryGraphDto[];
    offset: number;
    loading: boolean;
    loadingGraph: boolean;
}

class UserAwardsAccountStatement extends React.PureComponent<CommonProps, IUserAwardsAccountStatementComponentState> {

    constructor(props: CommonProps) {
        super(props);

        this.state = {
            data: null,
            graphData: null,
            dialog: null,
            offset: null,
            loading: true,
            loadingGraph: true,
        };
    }

    ///
    private handleCloseDialog = () => {
        this.setState({ dialog: null });
    };

    ///
    private openPublication(publicationId: string, publicationType: PublicationType, parentId: string = null) {
        return getPublicationSecondViewAction(publicationType, publicationId, parentId)();
    }

    ///
    private openTopic(topicId: string) {
        return getTopicSecondViewAction(topicId)();
    }

    ///
    private openBadge(badgeId: string) {
        this.setState({
            dialog: <WonBadgeInfoDialog userId={this.props.userId} badgeId={badgeId} onClose={this.handleCloseDialog} />
        });
    }

    ///
    private loadData(offset: number) {

        this.setState({ dialog: null, loading: true });

        api.getUserAwardStatement(this.props.userId, offset, DefaultListParameters.viewRecordsByPage)
            .then((result) => {
                this.setState({ data: result, loading: false, offset: offset });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false, offset: 0 });
            });
    }

    ///
    private loadGraph() {

        this.setState({ dialog: null, loadingGraph: true });

        api.getUserAwardGraphStatement(this.props.userId)
            .then((result) => {
                this.setState({ graphData: result, loadingGraph: false });
            })
            .catch((error) => {
                this.setState({ graphData: null, loadingGraph: false });
            });
    }

    ///
    private renderPlotGraph() {

        const { graphData } = this.state;

        var ds: number[] = [];
        var labels: string[] = [];
        var backgroundColors: string[] = [];
        var y = 0;

        graphData.forEach((v, index) => {
            var year = Math.trunc(v.yearMonth / 100);
            var month = v.yearMonth % 100;
            var dt = moment(`${year}-${month}-1`);
            if (y != year) { labels.push(dt.format("MMM/YYYY")); y = year; }
            else labels.push(dt.format("MMM"));

            if (index == graphData.length - 1) backgroundColors.push("rgba(255, 165, 0, 0.5)");
            else backgroundColors.push("orange");

            ds.push(v.points);
        });

        return (
            <div style={{ position: "relative", height: ChartJsHeight, width: "100%" }}>
                <Bar
                    data={
                        {
                            labels: labels,
                            datasets: [
                                {

                                    label: null,
                                    backgroundColor: backgroundColors,
                                    borderColor: '#000',
                                    borderWidth: 1,
                                    hoverBackgroundColor: 'rgba(128, 128, 128, 0.5)',
                                    hoverBorderColor: 'rgba(0, 0, 0, 0.5)',
                                    data: ds
                                }
                            ]
                        }
                    }
                    options={
                        {
                            ...ChartJsOptions,
                            plugins: {
                                legend: {
                                    display: false
                                }
                            }
                        }
                    }
                />
            </div>

        );
    }

    ///
    private renderGraph() {

        return (
            <MuiContainer margin="normal">

                <MuiContainer margin="dense" >
                    <Typography variant="h6"><b>{this.props.t("Views.Dashboards.AwardsAndCredits.UserAwardsAccountStatement.HeaderTitle")}</b></Typography>
                </MuiContainer>

                {
                    (this.state.loadingGraph)
                        ? <SpinnerIndicator />
                        : (!this.state.loadingGraph && this.state.graphData == null)
                            ? <AppMessageBox resource="LoadGraphDataFailed" />
                            : this.renderPlotGraph()
                }

            </MuiContainer>
        );
    }

    ///
    private renderList() {
        if (this.state.graphData == null)
            return null;

        if (this.state.loading)
            return <SpinnerIndicator />;

        if (!this.state.loading && this.state.data == null)
            return <AppMessageBox resource="LoadListDataFailed" />;

        if (this.state.data.total == 0)
            return <MuiMessageBox variant="warning" message={this.props.t("Views.Dashboards.AwardsAndCredits.UserAwardsAccountStatement.EmptyData")} />;

        return (
            <>
                <MuiContainer>
                    <Table>
                        <TableHead className={this.props.classes.tableHead}>
                            <TableRow>
                                <TableCell style={{ width: 1 }}>{this.props.t("Views.Dashboards.AwardsAndCredits.UserAwardsAccountStatement.DateText")}</TableCell>
                                <TableCell>{this.props.t("Views.Dashboards.AwardsAndCredits.UserAwardsAccountStatement.ReasonText")}</TableCell>
                                <TableCell>{this.props.t("Views.Dashboards.AwardsAndCredits.UserAwardsAccountStatement.ReferenceText")}</TableCell>
                                <TableCell style={{ width: 1 }}>{this.props.t("Views.Dashboards.AwardsAndCredits.UserAwardsAccountStatement.PointsText")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                this.state.data.items.map((v, index) => {
                                    return (
                                        <TableRow key={index}>
                                            <TableCell style={{ width: 1 }}>{moment(v.creationDate).format("D/MMM")}</TableCell>
                                            <TableCell><AwardTypeName awardType={v.awardType} /></TableCell>
                                            <TableCell>
                                                {
                                                    v.publicationTitle
                                                        ? <MuiLink onClick={() => this.openPublication(v.publicationId, v.publicationType, v.questionId)}>{v.publicationTitle}</MuiLink>
                                                        : v.topicName
                                                            ? <MuiLink onClick={() => this.openTopic(v.topicId)}>{v.topicName}</MuiLink>
                                                            : v.badgeName
                                                                ? <MuiLink onClick={() => this.openBadge(v.badgeId)}>{v.badgeName}</MuiLink>
                                                                : <span>&nbsp;</span>
                                                }
                                            </TableCell>
                                            <TableCell align="right" >{v.points}</TableCell>
                                        </TableRow>
                                    );
                                })
                            }
                        </TableBody>
                    </Table>
                </MuiContainer>
                <MuiPagination
                    limit={DefaultListParameters.viewRecordsByPage}
                    offset={this.state.offset}
                    total={this.state.data.total}
                    onClick={(e, offset) => this.loadData(offset)}
                />
            </>
        );
    }

    ///
    public componentDidMount() {
        this.loadData(0);
        this.loadGraph();
    }

    ///
    public render() {
        return (
            <MuiPaper borderVariant="accentedRound" borderColor="warning">
                {this.state.dialog}
                {this.renderGraph()}
                {this.renderList()}
            </MuiPaper>
        );
    }
}

////////////////////////////////////////////////////////////////////////////////////////////////////

interface IUserAwardsComponentState {
    menuAnchorEl: any;
    data: AwardRankingListResult;
    dialog: any;
    monthIndex: number;
    offset: number;
    loading: boolean;
}

class UserAwards extends React.PureComponent<CommonProps, IUserAwardsComponentState> {

    private yearMonth: MonthYear[] = [];

    constructor(props: CommonProps) {
        super(props);

        this.buildFilterTable();

        this.state = {
            data: null,
            dialog: null,
            offset: null,
            loading: true,
            menuAnchorEl: null,
            monthIndex: 1,
        };
    }

    ///
    private buildFilterTable() {
        var dt = moment();
        this.yearMonth.push({ key: 0, text: this.props.t("Views.Dashboards.AwardsAndCredits.UserAwards.AllHistory") });
        for (var i = 0; i < 12; i++) {
            dt = moment().subtract(i, "month");
            var text = i == 0 ? this.props.t("Views.Dashboards.Common.ThisMonth") : `${dt.format('MMM')}/${dt.format('YY')}`;
            this.yearMonth.push({ key: dt.year() * 100 + (dt.month() + 1), text });
        }
    }

    ///
    private loadData(offset: number) {

        this.setState({ dialog: null, loading: true, menuAnchorEl: null });

        api.getUserAwardRankingList(this.props.userId, this.yearMonth[this.state.monthIndex].key, offset, DefaultListParameters.viewRecordsByPage)
            .then((result) => {
                this.setState({ data: result, loading: false, offset: offset });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false, offset: 0 });
            });
    }

    ///
    private handleMonth(index: number) {
        this.setState({ menuAnchorEl: null, monthIndex: index }, () => this.loadData(0));
    }

    ///
    private handleCloseMenu() {
        this.setState({ menuAnchorEl: null });
    }

    ///
    private handleCloseDialog = () => {
        this.setState({ dialog: null });
    };

    ///
    private openDialog(id: string) {
        this.setState({
            dialog: (
                <Dialog open fullWidth maxWidth="md">
                    <UserShortProfile userId={id} onClose={this.handleCloseDialog} />
                </Dialog>
            )
        });
    }

    ///
    private renderUser() {

        const { data, monthIndex } = this.state;

        return (
            <MuiContainer margin="normal">
                <MuiPaper borderVariant="accentedRound" borderColor="warning">
                    <Grid container spacing={2} justifyContent="space-between" alignItems="center">
                        <Grid item>
                            <Typography variant="h6"><b>{this.props.t("Views.Dashboards.AwardsAndCredits.UserAwards.HeaderTitle")}</b></Typography>
                        </Grid>
                        {
                            !this.state.loading && this.state.data &&
                            <Grid item>
                                <ButtonDropdown
                                    text={this.yearMonth[monthIndex].text}
                                    onClick={
                                        (e) => {
                                            this.setState({ menuAnchorEl: e.currentTarget });
                                        }
                                    }
                                />
                                <Menu
                                    open={this.state.menuAnchorEl != null}
                                    anchorEl={this.state.menuAnchorEl}
                                    onClose={() => { this.handleCloseMenu(); }}
                                >
                                    {
                                        this.yearMonth.map((v, index) => <MenuItem key={index} onClick={() => this.handleMonth(index)}>{v.text}</MenuItem>)
                                    }
                                </Menu>
                            </Grid>
                        }
                    </Grid>

                    {
                        this.state.loading
                            ? <SpinnerIndicator />
                            : this.state.data == null
                                ? <AppMessageBox resource="LoadListDataFailed" />
                                : (
                                    <>
                                        <MuiContainer>
                                            <MuiColor variant="warning">
                                                <Grid container spacing={2} justifyContent="space-between" >
                                                    <Grid item>

                                                        <Grid container alignItems="center" className={GlobalClassKeys.textCenter} spacing={3}>
                                                            <Grid item>
                                                                <IconTrophyAlt size="5x" />
                                                            </Grid>
                                                            <Grid item>
                                                                {
                                                                    data.userRank
                                                                        ? (
                                                                            <>
                                                                                <Typography variant="h5">
                                                                                    <b>#{data.userRank.position}</b>
                                                                                </Typography>

                                                                                {monthIndex > 1 && <span>{Formaters.capitalize(this.props.t("Glossary.In"))}</span>}
                                                                                <span> {this.yearMonth[monthIndex].text}</span>
                                                                            </>
                                                                        )
                                                                        : <span>{this.props.t("Views.Dashboards.AwardsAndCredits.UserAwards.NoGainPointsText")}</span>
                                                                }
                                                            </Grid>
                                                        </Grid>

                                                    </Grid>
                                                    <Grid item>
                                                        <Grid container alignItems="center" className={GlobalClassKeys.textCenter} spacing={3}>
                                                            <Grid item>
                                                                <IconAwardPoints size="5x" />
                                                            </Grid>
                                                            <Grid item>
                                                                {
                                                                    data.userRank
                                                                        ? <><Typography variant="h5"><b>{data.userRank.points}</b></Typography><span>Pontos</span></>
                                                                        : <span>{this.props.t("Views.Dashboards.AwardsAndCredits.UserAwards.NoScoreText")}</span>
                                                                }
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </MuiColor>
                                        </MuiContainer>
                                        {this.renderList()}
                                    </>
                                )
                    }
                </MuiPaper>
            </MuiContainer>
        );
    }

    ///
    private renderList() {
        const { data: { rankingList } } = this.state;
        return (
            <MuiContainer margin="normal">
                {
                    rankingList.total == 0
                        ? <MuiMessageBox variant="warning" message={this.props.t("Views.Dashboards.AwardsAndCredits.UserAwards.EmptyData")} />
                        : (
                            <>
                                <MuiContainer>
                                    <Table>
                                        <TableBody>
                                            {
                                                this.state.data.rankingList.items.map((v, index) => {

                                                    return (
                                                        <TableRow key={index}>
                                                            <TableCell style={{ width: 1 }}>{v.position}</TableCell>
                                                            <TableCell style={{ width: 1 }}><UserProfileImageLink user={v} /></TableCell>
                                                            <TableCell><UserFullProfileLink user={v} color="secondary" /></TableCell>
                                                            <TableCell align="right" >{v.points}</TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            }
                                        </TableBody>
                                    </Table>
                                </MuiContainer>
                                <MuiPagination
                                    limit={DefaultListParameters.viewRecordsByPage}
                                    offset={this.state.offset}
                                    total={this.state.data.rankingList.total}
                                    onClick={(e, offset) => this.loadData(offset)}
                                />
                            </>
                        )
                }
            </MuiContainer>
        );
    }

    ///
    public componentDidMount() {
        this.loadData(null);
    }

    ///
    public render() {
        return (
            <>
                {this.state.dialog}
                {this.renderUser()}
            </>
        );
    }
}

////////////////////////////////////////////////////////////////////////////////////////////////////

interface IUserWonBadgesState {
    dialog: any;
    loading: boolean;
    data: UserBadgeDto[];
}

const wonBadgesStyles = (theme: Theme) => createStyles({
    badgeContainer: {
        display: "inline-table",
        float: "none",
    },
    badgeItem: {
        float: "left",
        marginBottom: theme.spacing(1),
    },
});

type IUserWonBadgesProps = CommonProps & WithStyles<typeof wonBadgesStyles>;

class UserWonBadgesComponent extends React.PureComponent<IUserWonBadgesProps, IUserWonBadgesState>
{
    constructor(props: IUserWonBadgesProps) {
        super(props);
        this.state = { data: null, dialog: null, loading: true };
    }

    private loadData() {
        api.getUserAllBadges(this.props.userId)
            .then((result) => {
                this.setState({ data: result, loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false });
            });
    }

    private openBadge(badgeId: string) {
        this.setState({
            dialog: <WonBadgeInfoDialog
                userId={this.props.userId}
                badgeId={badgeId}
                onClose={
                    () => {
                        this.setState({ dialog: null });
                    }
                } />
        });
    }


    public componentDidMount() {
        this.loadData();
    }

    public render() {
        const { data, dialog, loading } = this.state;

        if (!loading && data && data.length == 0)
            return null;

        return (
            <MuiContainer margin="normal" >

                {dialog}
                <br />
                <MuiPaper borderVariant="accentedRound" borderColor="warning">
                    <MuiContainer margin="dense" >
                        <Typography variant="h6"><b>{this.props.t("Views.Dashboards.AwardsAndCredits.UserWonBadges.HeaderTitle")}</b></Typography>
                    </MuiContainer>

                    {
                        loading
                            ? <SpinnerIndicator />
                            : data == null
                                ? <AppMessageBox resource="LoadListDataFailed" />
                                : (
                                    <div className={this.props.classes.badgeContainer}>
                                        {
                                            data.map((v, i) => (
                                                <MuiClickable key={i} onClick={() => this.openBadge(v.badgeId)} className={this.props.classes.badgeItem} >
                                                    <MuiAvatar
                                                        title={v.name}
                                                        backgroundColor="transparent"
                                                        borderColor={v.wonTotalPoints != null ? "warning" : "muted"}
                                                    >
                                                        <img src={v.thumbnailUrl} alt={v.name} width={32} height={32} style={{ filter: v.wonTotalPoints != null ? "none" : "grayscale(1)" }} />
                                                    </MuiAvatar>
                                                </MuiClickable>
                                            ))
                                        }
                                    </div>
                                )
                    }
                </MuiPaper>
            </MuiContainer>
        );
    }
}

const UserWonBadges = withStyles(wonBadgesStyles)(UserWonBadgesComponent);

////////////////////////////////////////////////////////////////////////////////////////////////////


class UserAwardsComponent extends React.PureComponent<CommonProps>  {
    render() {
        return (
            <>
                <UserAwards {...this.props} />
                <UserWonBadges {...this.props} />
                <UserAwardsAccountStatement {...this.props} />
            </>
        );
    };
}

export default withTranslation()(UserAwardsComponent);